export type Coordinates = { latitude: number; longitude: number }

let pending: Promise<Coordinates | null> | null = null

/**
 * The browser position, asked once per page load and shared by every caller.
 * Resolves to null (never rejects) when the API is missing, the user refuses
 * or the lookup fails: callers then behave as if no position was known.
 * Only a refusal (or a missing API) is remembered; a timeout or an
 * unavailable position is asked again on the next call.
 */
export function getCurrentCoordinates(): Promise<Coordinates | null> {
  if (pending) {
    return pending
  }
  let retryable = false
  const request: Promise<Coordinates | null> = new Promise((resolve) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      resolve(null)
      return
    }
    navigator.geolocation.getCurrentPosition(
      ({ coords }) =>
        resolve({ latitude: coords.latitude, longitude: coords.longitude }),
      (error) => {
        // Anything but PERMISSION_DENIED (timeout, position unavailable).
        retryable = error?.code !== 1
        resolve(null)
      },
      { timeout: 10000 }
    )
  })
  pending = request
  request.then(() => {
    if (retryable && pending === request) {
      pending = null
    }
  })
  return pending
}

/**
 * Whether the position can be read without prompting the user. False when
 * the Permissions API is missing: callers then wait for a user gesture.
 */
export async function isGeolocationGranted(): Promise<boolean> {
  try {
    const status = await navigator.permissions?.query({ name: 'geolocation' })
    return status?.state === 'granted'
  } catch {
    return false
  }
}

/** Test helper: forget the cached position. */
export function resetCoordinatesCache(): void {
  pending = null
}
